import logo from '@/assets/hiredcoach-logo.svg';
import UserAvatar from '@/components/avatar/UserAvatar';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/contexts/AuthContext';
import { InterviewResponse, useApiClient } from '@/lib/api';
import { getUserErrorMessage } from '@/lib/error-messages';
import { ArrowLeft, ArrowRight, Briefcase, Lightbulb, Mic, Pause, Play, RotateCcw, StopCircle } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

// Custom Audio Player Button
const AudioPlayerButton: React.FC<{ src: string; className?: string }> = ({ src, className }) => {
    const audioRef = React.useRef<HTMLAudioElement>(null);
    const [status, setStatus] = React.useState<'idle' | 'playing' | 'paused' | 'ended'>('idle');

    React.useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;
        const handleEnded = () => setStatus('ended');
        audio.addEventListener('ended', handleEnded);
        return () => {
            audio.removeEventListener('ended', handleEnded);
        };
    }, []);

    const handlePlay = () => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.play();
        setStatus('playing');
    };

    const handlePause = () => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.pause();
        setStatus('paused');
    };

    const handleReset = () => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.currentTime = 0;
        audio.play();
        setStatus('playing');
    };

    // Sync state if user interacts with native controls (fallback)
    const handleAudioPlay = () => setStatus('playing');
    const handleAudioPause = () => setStatus('paused');

    return (
        <div className={className || "flex items-center justify-center"}>
            <audio
                ref={audioRef}
                src={src}
                style={{ display: 'none' }}
                onPlay={handleAudioPlay}
                onPause={handleAudioPause}
            />
            {status === 'idle' || status === 'paused' ? (
                <button
                    className="w-16 h-16 rounded-full bg-primary text-white hover:bg-primary/90 transition flex items-center justify-center shadow focus:outline-none"
                    onClick={handlePlay}
                    aria-label="Play audio"
                >
                    <Play className="w-8 h-8" />
                </button>
            ) : status === 'playing' ? (
                <button
                    className="w-16 h-16 rounded-full bg-secondary text-primary hover:bg-secondary/80 transition flex items-center justify-center shadow focus:outline-none"
                    onClick={handlePause}
                    aria-label="Pause audio"
                >
                    <Pause className="w-8 h-8" />
                </button>
            ) : (
                <button
                    className="w-16 h-16 rounded-full bg-primary text-white hover:bg-primary/90 transition flex items-center justify-center shadow focus:outline-none"
                    onClick={handleReset}
                    aria-label="Restart audio"
                >
                    <RotateCcw className="w-8 h-8" />
                </button>
            )}
        </div>
    );
};


interface InterviewExamProps {
    interview_response: InterviewResponse;
    // When true, the component runs in demo mode: no API calls, no mic access,
    // and no navigation side-effects. Useful for landing page demos.
    demoMode?: boolean;
    embed?: boolean;
}

// The page where you actually take the interview
const InterviewExam: React.FC<InterviewExamProps> = ({ interview_response, demoMode = false, embed = false }) => {
    const navigate = useNavigate();
    const { api } = useApiClient();
    const { token: authToken, user } = useAuth();

    // Demo mode flag
    const isDemo = demoMode;
    const isEmbed = !!embed;

    const [interview, setInterview] = useState<InterviewResponse | null>(null);

    const [currentIdx, setCurrentIdx] = useState<number>(-1); // -1 = intro, N = question, len = end
    const [showHints, setShowHints] = useState<Record<string, boolean>>({});
    const [loading, setLoading] = useState<boolean>(true);
    const [initialAnswers, setInitialAnswers] = useState<Record<string, string>>({});
    const [answers, setAnswers] = useState<Record<string, string>>({});

    // Unsaved changes tracking
    const [showUnsavedWarning, setShowUnsavedWarning] = useState<boolean>(false);
    const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
    
    // Submit warning for incomplete interviews
    const [showSubmitWarning, setShowSubmitWarning] = useState<boolean>(false);
    const answerTextareaRef = useRef<HTMLTextAreaElement | null>(null);
    const resolvedInterview = interview ?? interview_response ?? null;

    useEffect(() => {
        if (!interview && interview_response) {
            setInterview(interview_response);
        }
    }, [interview, interview_response]);

    useEffect(() => {
        if (!resolvedInterview) return;

        const answerMap: Record<string, string> = {};
        resolvedInterview.questions.forEach((q) => {
            answerMap[q.Question.id] = q.Answer?.response || '';
        });
        setAnswers(answerMap);
        setInitialAnswers(answerMap);

        // Decide which question to show on load
        const order = resolvedInterview.question_order;
        let firstUnansweredIdx = -1;
        for (let i = 0; i < order.length; i++) {
            if (!(answerMap[order[i]] || '').trim()) {
                firstUnansweredIdx = i;
                break;
            }
        }
        if (Object.values(answerMap).every(ans => !(ans || '').trim())) {
            // No answers, start at intro
            setCurrentIdx(-1);
        } else if (firstUnansweredIdx !== -1) {
            // Some answered, go to first unanswered
            setCurrentIdx(firstUnansweredIdx);
        } else {
            // All answered, go to last question
            setCurrentIdx(order.length - 1);
        }
    }, [resolvedInterview]);

    // Check if there are unsaved changes
    const hasUnsavedChanges = useCallback(() => {
        if (!resolvedInterview) return false;
        return resolvedInterview.question_order.some(qid => {
            const currentAnswer = (answers[qid] || '').trim();
            const initialAnswer = (initialAnswers[qid] || '').trim();
            return currentAnswer !== initialAnswer && currentAnswer !== '';
        });
    }, [resolvedInterview, answers, initialAnswers]);

    // Save all unsaved changes
    const saveAllChanges = useCallback(async () => {
        if (!resolvedInterview) return;

        const unsavedQuestions = resolvedInterview.question_order.filter(qid => {
            const currentAnswer = (answers[qid] || '').trim();
            const initialAnswer = (initialAnswers[qid] || '').trim();
            return currentAnswer !== initialAnswer && currentAnswer !== '';
        });

        if (unsavedQuestions.length === 0) return;

        try {
            setLoading(true);
            if (!isDemo) {
                // Save all unsaved answers
                await Promise.all(
                    unsavedQuestions.map(qid =>
                        api.saveInterviewAnswer(resolvedInterview.id, qid, answers[qid].trim())
                    )
                );
            }

            // Update initial answers to match current answers
            const newInitialAnswers = { ...initialAnswers };
            unsavedQuestions.forEach(qid => {
                newInitialAnswers[qid] = answers[qid].trim();
            });
            setInitialAnswers(newInitialAnswers);
            if (!isDemo) {
                toast.success('All changes saved successfully!');
            }
        } catch (e: any) {
            toast.error(getUserErrorMessage(e, 'Unable to save changes right now. Please try again later.'));
            throw e;
        } finally {
            setLoading(false);
        }
    }, [resolvedInterview, answers, initialAnswers, api, isDemo]);

    // Handle beforeunload event to warn about unsaved changes (for page refresh/close)
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (hasUnsavedChanges()) {
                e.preventDefault();
                e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
                return e.returnValue;
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges]);

    // Custom navigation interceptor for BrowserRouter
    const location = useLocation();
    
    useEffect(() => {
        if (isDemo) return; // Disable navigation interception in demo mode

        // Intercept link clicks and navigation attempts
        const handleLinkClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const link = target.closest('a[href]') as HTMLAnchorElement;
            
            if (link && hasUnsavedChanges()) {
                // Check if it's an internal link (not external or mailto/tel)
                const href = link.getAttribute('href');
                if (href && !href.startsWith('http') && !href.startsWith('mailto:') && !href.startsWith('tel:')) {
                    e.preventDefault();
                    e.stopPropagation();
                    
                    setPendingNavigation(() => () => {
                        // Navigate to the intended destination
                        if (href.startsWith('/')) {
                            navigate(href);
                        } else {
                            window.location.href = href;
                        }
                    });
                    setShowUnsavedWarning(true);
                }
            }
        };
        
        // Intercept browser back/forward navigation
        const handlePopState = (e: PopStateEvent) => {
            if (hasUnsavedChanges()) {
                // Push the current state back to prevent navigation
                window.history.pushState(null, '', location.pathname + location.search);
                
                setPendingNavigation(() => () => {
                    // Go back after user confirms
                    window.history.back();
                });
                setShowUnsavedWarning(true);
            }
        };
        
        document.addEventListener('click', handleLinkClick, true);
        window.addEventListener('popstate', handlePopState);
        
        return () => {
            document.removeEventListener('click', handleLinkClick, true);
            window.removeEventListener('popstate', handlePopState);
        };
    }, [hasUnsavedChanges, navigate, location, isDemo]);

    // Navigation handlers
    const goToNext = async () => {
        if (!resolvedInterview || currentIdx < 0 || currentIdx >= resolvedInterview.questions.length) {
            setCurrentIdx((idx) => idx + 1);
            return;
        }
        const qid = resolvedInterview.question_order[currentIdx];
        const currentAnswer = (answers[qid] || '').trim();
        const initialAnswer = (initialAnswers[qid] || '').trim();
        // Only save if user entered a new answer
        if (currentAnswer && currentAnswer !== initialAnswer) {
            try {
                setLoading(true);
                if (!isDemo) {
                    await api.saveInterviewAnswer(resolvedInterview.id, qid, currentAnswer);
                }
                setInitialAnswers((prev) => ({ ...prev, [qid]: currentAnswer }));
            } catch (e: any) {
                toast.error(getUserErrorMessage(e, 'Unable to save answer right now. Please try again later.'));
            } finally {
                setLoading(false);
                setCurrentIdx((idx) => idx + 1);
            }
        } else {
            setCurrentIdx((idx) => idx + 1);
        }
    };

    const goToPrev = () => {
        setCurrentIdx((idx) => idx - 1);
    };

    // Answer change
    const handleAnswerChange = (qid: string, value: string) => {
        setAnswers((prev) => ({ ...prev, [qid]: value }));
    };

    useEffect(() => {
        if (!resolvedInterview || currentIdx < 0 || currentIdx >= resolvedInterview.question_order.length) {
            return;
        }

        const textarea = answerTextareaRef.current;
        if (!textarea) return;

        textarea.style.height = 'auto';
        const maxHeight = Math.max(320, Math.min(window.innerHeight * 0.6, 560));
        textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
        textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
        textarea.scrollTop = textarea.scrollHeight;
    }, [answers, currentIdx, resolvedInterview]);

    // Speech-to-text mic logic
    const [isRecording, setIsRecording] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [recorder, setRecorder] = useState<MediaRecorder | null>(null);
    const [activeQid, setActiveQid] = useState<string | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const [audioChunks, setAudioChunks] = useState<Blob[]>([]);
    const [micLevel, setMicLevel] = useState(0);
    const audioContextRef = useRef<AudioContext | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const levelAnimationFrameRef = useRef<number | null>(null);

    // Recording timer
    const [recordingTime, setRecordingTime] = useState(0);
    const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);

    const stopMicLevelTracking = useCallback(() => {
        if (levelAnimationFrameRef.current !== null) {
            cancelAnimationFrame(levelAnimationFrameRef.current);
            levelAnimationFrameRef.current = null;
        }
        analyserRef.current = null;
        if (audioContextRef.current) {
            audioContextRef.current.close().catch(() => undefined);
            audioContextRef.current = null;
        }
        setMicLevel(0);
    }, []);

    // Cleanup recording timer and mic tracking on unmount
    useEffect(() => {
        return () => {
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
            }
            stopMicLevelTracking();
        };
    }, [stopMicLevelTracking]);

    // Check if microphone is supported
    const isMicrophoneSupported = () => {
        const hasMediaDevices = !!(navigator.mediaDevices);
        const hasGetUserMedia = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
        const hasMediaRecorder = !!(window.MediaRecorder);

        return hasMediaDevices && hasGetUserMedia && hasMediaRecorder;
    };

    // Detect browser type for mobile-specific handling
    const getBrowserInfo = () => {
        const userAgent = navigator.userAgent;
        const isIOS = /iPad|iPhone|iPod/.test(userAgent);
        const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
        const isChrome = /Chrome/.test(userAgent);
        const isFirefox = /Firefox/.test(userAgent);
        const isMobile = /Mobi|Android/i.test(userAgent) || isIOS;

        return { isIOS, isSafari, isChrome, isFirefox, isMobile };
    };

    // Get supported audio format for the device
    const getSupportedAudioFormat = () => {
        const { isIOS, isSafari, isChrome, isFirefox } = getBrowserInfo();

        // Safari/iOS prefers different formats
        const formats = isIOS || isSafari ? [
            'audio/mp4',
            'audio/wav',
            'audio/webm;codecs=opus',
            'audio/webm',
            'audio/ogg;codecs=opus'
        ] : isChrome ? [
            'audio/webm;codecs=opus',
            'audio/webm',
            'audio/mp4',
            'audio/ogg;codecs=opus',
            'audio/wav'
        ] : isFirefox ? [
            'audio/ogg;codecs=opus',
            'audio/webm;codecs=opus',
            'audio/webm',
            'audio/mp4',
            'audio/wav'
        ] : [
            'audio/webm;codecs=opus',
            'audio/webm',
            'audio/mp4',
            'audio/ogg;codecs=opus',
            'audio/wav'
        ];

        for (const format of formats) {
            if (MediaRecorder.isTypeSupported(format)) {
                return format;
            }
        }
        return isIOS || isSafari ? 'audio/mp4' : 'audio/webm'; // browser-specific fallback
    };

    const handleMicClick = async (qid: string) => {
        // Check if microphone is supported
        if (isDemo) {
            // Demo mode: silent no-op
            return;
        }
        if (!isMicrophoneSupported()) {
            toast.error('Microphone is not supported on this device or browser.');
            return;
        }

        if (isRecording) {
            // Stop recording
            recorder?.stop();
            setIsRecording(false);
            stopMicLevelTracking();
            // Clear recording timer
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
                recordingTimerRef.current = null;
            }
            setRecordingTime(0);
        } else {
            try {
                // Start recording
                setActiveQid(qid);
                audioChunksRef.current = [];
                setAudioChunks([]);

                // Get browser info once for the entire function
                const { isIOS, isSafari, isMobile } = getBrowserInfo();

                const audioConstraints = {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    // Safari/iOS can be picky about sample rates
                    ...(isIOS || isSafari ? {
                        sampleRate: { ideal: 48000, min: 16000, max: 48000 },
                        channelCount: { ideal: 1 },
                        volume: { ideal: 1.0 }
                    } : {
                        sampleRate: 44100
                    })
                };

                const stream = await navigator.mediaDevices.getUserMedia({
                    audio: audioConstraints
                });

                const AudioContextCtor = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
                if (!AudioContextCtor) {
                    throw new Error('AudioContext unavailable');
                }
                const audioContext = new AudioContextCtor();
                const analyser = audioContext.createAnalyser();
                analyser.fftSize = 256;
                const source = audioContext.createMediaStreamSource(stream);
                source.connect(analyser);
                audioContextRef.current = audioContext;
                analyserRef.current = analyser;

                const sampleBuffer = new Uint8Array(analyser.frequencyBinCount);
                const updateMicLevel = () => {
                    if (!analyserRef.current) return;
                    analyserRef.current.getByteFrequencyData(sampleBuffer);
                    const average = sampleBuffer.reduce((sum, value) => sum + value, 0) / sampleBuffer.length;
                    setMicLevel(Math.min(1, average / 90));
                    levelAnimationFrameRef.current = requestAnimationFrame(updateMicLevel);
                };
                updateMicLevel();

                // Get supported audio format
                const audioFormat = getSupportedAudioFormat();

                // Create MediaRecorder with mobile-optimized settings
                const recorderOptions: MediaRecorderOptions = {
                    mimeType: audioFormat
                };

                // Add bitrate for mobile optimization
                if (isMobile) {
                    recorderOptions.audioBitsPerSecond = 128000; // Lower bitrate for mobile
                }

                const mediaRecorder = new MediaRecorder(stream, recorderOptions);

                setRecorder(mediaRecorder);

                mediaRecorder.ondataavailable = (e) => {
                    if (e.data.size > 0) {
                        audioChunksRef.current.push(e.data);
                        setAudioChunks([...audioChunksRef.current]);
                    }
                };

                mediaRecorder.onstop = async () => {
                    stopMicLevelTracking();
                    stream.getTracks().forEach(track => {
                        track.stop();
                    });

                    // Clear recording timer
                    if (recordingTimerRef.current) {
                        clearInterval(recordingTimerRef.current);
                        recordingTimerRef.current = null;
                    }
                    setRecordingTime(0);

                    if (audioChunksRef.current.length === 0) {
                        console.error('No audio data recorded');
                        toast.error('No audio was recorded. Please try again.');
                        return;
                    }

                    const audioBlob = new Blob(audioChunksRef.current, { type: audioFormat });

                    if (audioBlob.size === 0) {
                        console.error('Audio blob is empty');
                        toast.error('Recording failed. Please try again.');
                        return;
                    }

                    setIsTranscribing(true);
                    try {
                        const formData = new FormData();
                        // Use appropriate file extension based on format
                        const extension = audioFormat.includes('webm') ? 'webm' :
                            audioFormat.includes('mp4') ? 'mp4' :
                                audioFormat.includes('ogg') ? 'ogg' : 'wav';
                        formData.append('file', audioBlob, `recording.${extension}`);

                        const headers: Record<string, string> = {};
                        if (authToken) {
                            headers['Authorization'] = `Bearer ${authToken}`;
                        }

                        const resp = await fetch('/api/interviews/stt', {
                            method: 'POST',
                            body: formData,
                            headers,
                        });

                        if (!resp.ok) {
                            const errorText = await resp.text();
                            throw new Error(`Speech-to-text failed: ${resp.status} ${errorText}`);
                        }

                        let text = await resp.text();
                        text = text.replace(/^(["'])(.*)\1$/, '$2');

                        if (!text.trim()) {
                            toast.error('No speech was detected. Please try speaking more clearly.');
                            return;
                        }

                        setAnswers(prev => {
                            const prevText = prev[qid] || '';
                            // Append as new paragraph with blank line if previous text exists
                            const newText = prevText.trim()
                                ? prevText.trimEnd() + '\n\n' + text.trim()
                                : text.trim();
                            return { ...prev, [qid]: newText };
                        });

                        toast.success('Speech converted to text successfully!');
                    } catch (err: any) {
                        console.error('Speech-to-text error:', err);
                        toast.error(getUserErrorMessage(err, 'Unable to transcribe audio right now. Please try again later.'));
                    } finally {
                        setIsTranscribing(false);
                    }
                };

                mediaRecorder.onerror = (event: any) => {
                    console.error('MediaRecorder error:', event);
                    toast.error('Recording error occurred. Please try again.');
                    setIsRecording(false);
                    setActiveQid(null);
                    stopMicLevelTracking();
                    // Clear recording timer on error
                    if (recordingTimerRef.current) {
                        clearInterval(recordingTimerRef.current);
                        recordingTimerRef.current = null;
                    }
                    setRecordingTime(0);
                    stream.getTracks().forEach(track => track.stop());
                };

                // Start recording with mobile-optimized timing
                const timeslice = isMobile ? 500 : 1000; // More frequent data collection on mobile

                mediaRecorder.start(timeslice);
                setIsRecording(true);

                // Start recording timer
                setRecordingTime(0);
                recordingTimerRef.current = setInterval(() => {
                    setRecordingTime(prev => prev + 1);
                }, 1000);

            } catch (err: any) {
                console.error('Microphone access error:', err);
                let errorMessage = 'Could not access microphone.';

                if (err.name === 'NotAllowedError') {
                    errorMessage = 'Microphone permission denied. Please allow microphone access and try again.';
                } else if (err.name === 'NotFoundError') {
                    errorMessage = 'No microphone found. Please check your device settings.';
                } else if (err.name === 'NotSupportedError') {
                    errorMessage = 'Microphone is not supported on this browser.';
                } else if (err.name === 'NotReadableError') {
                    errorMessage = 'Microphone is being used by another application.';
                }

                toast.error(errorMessage);
                setActiveQid(null);
                stopMicLevelTracking();
                // Clear recording timer on error
                if (recordingTimerRef.current) {
                    clearInterval(recordingTimerRef.current);
                    recordingTimerRef.current = null;
                }
                setRecordingTime(0);
            }
        }
    };


    // Submit handler
    const handleSubmit = async () => {
        if (!resolvedInterview) return;

        // Check completion requirements
        const blankAnswers = resolvedInterview.question_order.filter(qid => !(answers[qid] && answers[qid].trim()));
        const totalQuestions = resolvedInterview.question_order.length;
        const answeredQuestions = totalQuestions - blankAnswers.length;
        const halfQuestions = Math.ceil(totalQuestions / 2);

        // If no questions answered, require at least half
        if (answeredQuestions === 0) {
            if (!isDemo) toast.error(`Please complete at least ${halfQuestions} questions before submitting.`);
            if (isDemo) return;
            return;
        }

        // If less than half answered, require at least half
        if (answeredQuestions < halfQuestions) {
            if (!isDemo) toast.error(`Please complete at least ${halfQuestions} questions before submitting. You have ${answeredQuestions} completed.`);
            if (isDemo) return;
            return;
        }

        // If some but not all answered, show confirmation modal
        if (blankAnswers.length > 0 && answeredQuestions >= halfQuestions) {
            if (!isDemo) setShowSubmitWarning(true);
            if (isDemo) return;
            return;
        }

        try {
            if (!isDemo) {
                await api.completeInterview(resolvedInterview.id);
                toast.success('Interview submitted successfully!');
                window.location.reload();
            } else {
                // Demo mode: silent no-op
            }
        } catch (e: any) {
            console.error(e)
            toast.error('Failed to complete interview - try again in a bit');
        }
    };

    // Handle unsaved changes confirmation
    const handleUnsavedConfirm = async () => {
        try {
            await saveAllChanges();
            setShowUnsavedWarning(false);
            if (pendingNavigation) {
                pendingNavigation();
                setPendingNavigation(null);
            }
        } catch (e) {
            // Error already handled in saveAllChanges
        }
    };

    const handleUnsavedCancel = () => {
        setShowUnsavedWarning(false);
        setPendingNavigation(null);
    };

    const handleUnsavedDiscard = () => {
        setShowUnsavedWarning(false);
        if (pendingNavigation) {
            pendingNavigation();
            setPendingNavigation(null);
        }
    };

    // Handle submit warning confirmation
    const handleSubmitConfirm = async () => {
        setShowSubmitWarning(false);
        if (!resolvedInterview) return;

        try {
            if (!isDemo) {
                await api.completeInterview(resolvedInterview.id);
                toast.success('Interview submitted successfully!');
                window.location.reload();
            } else {
                // Demo mode: silent no-op
            }
        } catch (e: any) {
            console.error(e)
            toast.error('Failed to complete interview - try again in a bit');
        }
    };

    const handleSubmitCancel = () => {
        setShowSubmitWarning(false);
    };

    // Function to render the current screen based on currentIdx
    const renderCurrentScreen = () => {
        if (!resolvedInterview) {
            return (
                <div className="flex min-h-[60vh] items-center justify-center">
                    <div className="text-sm text-muted-foreground">Loading interview...</div>
                </div>
            );
        }

        // Intro screen
        if (currentIdx === -1) {
            return (
            <div className="flex flex-col items-center justify-center min-h-[80vh] p-4 sm:px-4 animate-fade-in">
                <div className="bg-white dark:bg-card sm:rounded-2xl sm:shadow-xl p-4 sm:p-8 max-w-xl w-full flex flex-col items-center">
                    <h1 className="flex items-center gap-2 text-lg sm:text-xl text-primary font-semibold mb-2 text-center">Time to interview</h1>
                    <img src={logo} alt="HiredCoach Logo" className="w-20 sm:w-28 mb-4" />
                    <div className="flex items-center gap-2 text-lg sm:text-xl text-primary font-semibold mb-2 text-center">
                        You got this <span role="img" aria-label="flex">💪</span>
                    </div>
                    <div className="text-center text-muted-foreground mb-4 text-sm sm:text-base">
                        <span className="font-bold text-primary">{resolvedInterview.questions.length} questions</span> await! This is your chance to practice and improve your interviewing skills.
                    </div>
                    <ul className="text-left text-muted-foreground mb-6 space-y-2 text-xs sm:text-sm list-disc list-outside pl-6 sm:pl-8">
                        <li>Answer each question as best you can - type or speak your answers</li>
                        <li>You can pause and return later; your progress is saved until you decide to submit your answers</li>
                        <li>If you get stuck, you can choose to peek at helpful hints that tell you what the interviewer is looking for</li>
                        <li>After you finish, we'll grade your answers and give you constructive feedback</li>
                    </ul>
                <div className="flex flex-col sm:flex-row gap-3 w-full">
                    <button
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-md border font-medium text-muted-foreground hover:bg-muted/50 transition"
                        onClick={() => {
                            if (isDemo) return;
                            if (resolvedInterview.job_id) {
                                navigate(`/jobs/${resolvedInterview.job_id}`);
                                return;
                            }
                            navigate('/interviews');
                        }}
                        disabled={isDemo}
                    >
                        <Briefcase className="w-4 h-4" /> {resolvedInterview.job_id ? 'Back to Job' : 'Back to Interviews'}
                    </button>
                    <button
                        className="flex-1 px-6 py-3 rounded-md bg-primary text-white font-semibold shadow hover:bg-primary/90 transition"
                        onClick={() => setCurrentIdx(0)}
                    >
                        Start Interview
                    </button>
                </div>
            </div>
        </div>
    );
}

        // End confirmation screen
        if (currentIdx >= resolvedInterview.question_order.length) {
            // Check for blank answers
            const blankAnswers = resolvedInterview.question_order.filter(qid => !(answers[qid] && answers[qid].trim()));
            const totalQuestions = resolvedInterview.question_order.length;
            const answeredQuestions = totalQuestions - blankAnswers.length;
            const halfQuestions = Math.ceil(totalQuestions / 2);
            const hasBlankAnswers = blankAnswers.length > 0;
            const isEmpty = blankAnswers.length === totalQuestions;
            const hasInsufficientAnswers = answeredQuestions < halfQuestions;
            
            return (
                <div className="flex flex-col items-center justify-center min-h-[80vh] p-4 sm:px-4 animate-fade-in">
                    <div className="bg-white dark:bg-card sm:rounded-2xl sm:shadow-xl p-4 sm:p-8 max-w-xl w-full">
                        <img src={logo} alt="HiredCoach Logo" className="w-28 mb-4 mx-auto" />
                        
                        {/* No answers at all */}
                        {isEmpty && (
                            <>
                                <h1 className="text-xl sm:text-2xl text-primary font-semibold mb-2 text-center">Hold up!</h1>
                                <div className="text-center text-muted-foreground mb-6 text-sm sm:text-base">
                                    You haven't answered anything! You'll need to complete at least {halfQuestions} questions before submitting your interview.
                                </div>
                            </>
                        )}
                        
                        {/* Less than half answered */}
                        {!isEmpty && hasInsufficientAnswers && (
                            <>
                                <h1 className="text-xl sm:text-2xl text-primary font-semibold mb-2 text-center">Almost there!</h1>
                                <div className="text-center text-muted-foreground mb-4 text-sm sm:text-base">
                                    You've answered {answeredQuestions} out of {totalQuestions} questions.
                                </div>
                                <div className="text-center text-muted-foreground mb-6 text-sm sm:text-base">
                                    Please complete at least {halfQuestions} questions before submitting your interview.
                                </div>
                            </>
                        )}
                        
                        {/* At least half answered */}
                            {!isEmpty && !hasInsufficientAnswers && (
                                <>
                                    <h1 className="text-xl sm:text-2xl text-primary font-semibold mb-2 text-center">You've hit the end!</h1>
                                    <div className="text-center text-muted-foreground mb-6 text-sm sm:text-base">
                                        Congratulations on making it through the interview - that must have been tough!
                                    </div>
                                    <div className="text-center text-muted-foreground mb-6 text-sm sm:text-base">
                                        If you hit submit, your interview will be finished (and closed to further edits). We'll then grade
                                        your answers and generate advice for you!
                                    </div>
                                    {hasBlankAnswers && (
                                        <>
                                            <div className="flex items-center gap-3 bg-amber-50 border-l-4 border-amber-400 text-amber-900 px-4 py-3 rounded mb-6 w-full mx-auto">
                                                <svg className="w-6 h-6 text-amber-700 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.054 0 1.658-1.14 1.105-2.045l-6.928-12.01c-.526-.912-1.684-.912-2.21 0l-6.929 12.01c-.553.905.051 2.045 1.106 2.045z" />
                                                </svg>
                                                <div>
                                                    <span className="font-semibold">Notice:</span> You have {blankAnswers.length} unanswered questions. You can submit now or go back to complete them.
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </>
                            )}

                        <div className="flex flex-col gap-3 w-full">
                            {/* Navigation buttons */}
                            <div className="flex flex-col sm:flex-row gap-3 sm:gap-2 w-full">
                                <button
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-md border font-medium text-primary bg-white hover:bg-primary/10 transition order-2 sm:order-1"
                                    type="button"
                                    onClick={() => setCurrentIdx(resolvedInterview.question_order.length - 1)}
                                >
                                    <ArrowLeft className="w-5 h-5" /> Back to Questions
                                </button>
                                <button
                                    disabled={isEmpty || hasInsufficientAnswers}
                                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-primary text-white font-semibold shadow hover:bg-primary/90 transition order-1 sm:order-2 ${(isEmpty || hasInsufficientAnswers) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    onClick={handleSubmit}
                                >
                                    Submit Interview
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        // Main question screen
        const qid = resolvedInterview.question_order[currentIdx];
        const payload = resolvedInterview.questions.find((q) => q.Question.id === qid);
        const q = payload?.Question;
        const total = resolvedInterview.question_order.length;

        if (!q) return null;

        return (
            <div className={`${isEmbed ? 'flex flex-col items-center sm:px-0' : 'flex flex-col items-center min-h-screen bg-muted sm:px-4'} animate-fade-in`}>
        {/* Progress Bar */}
        <div className={`w-full max-w-2xl ${isEmbed ? 'mt-2 mb-2 px-0' : 'mt-4 sm:mt-8 mb-4 px-4 sm:px-0'}`}>
            <div className="text-xs sm:text-sm text-muted-foreground mb-1">
                Question {currentIdx + 1} of {total}
            </div>
            <div className="w-full h-2 sm:h-3 bg-gray-200 rounded-full overflow-hidden">
                <div
                    className="h-full bg-primary transition-all duration-300"
                    style={{ width: `${((currentIdx + 1) / total) * 100}%` }}
                />
            </div>
        </div>
        <div className={`w-full max-w-2xl bg-card sm:rounded-lg ${isEmbed ? 'shadow-sm p-4 sm:p-5 mt-0 mb-0 sm:mx-0' : 'sm:shadow p-4 sm:p-6 mt-2 mb-4 sm:mx-4'}`}>
            {payload.Audio?.url ? (
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 mb-4">
                    <div className="flex-1 order-2 sm:order-1">
                        <div className="text-base sm:text-lg font-bold mb-0 break-words">{q.question}</div>
                    </div>
                    <AudioPlayerButton src={payload.Audio.url} className="flex-shrink-0 order-1 sm:order-2 self-center sm:self-auto" />
                </div>
            ) : (
                <div className="text-base sm:text-lg font-bold mb-4 break-words">{q.question}</div>
            )}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-start">
                <div className="flex-1">
                    <div className="mb-2 flex items-center gap-2">
                        <UserAvatar user={user} className="h-8 w-8 sm:h-10 sm:w-10 border-0" />
                        <span className="text-sm sm:text-base font-medium text-gray-700">Your response</span>
                    </div>
                    <textarea
                        ref={answerTextareaRef}
                        className="w-full border rounded-md p-3 sm:p-4 min-h-[180px] sm:min-h-[240px] text-base sm:text-lg focus:outline-primary resize-vertical bg-background"
                        placeholder="Type your answer here..."
                        value={answers[qid] || ''}
                        onChange={e => handleAnswerChange(qid, e.target.value)}
                        style={{ fontFamily: 'inherit', maxHeight: '60vh' }}
                    />
                </div>
                <button
                    className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center transition focus:outline-none self-center sm:self-start sm:mt-0
                ${!isMicrophoneSupported() ? 'bg-gray-200 border border-gray-300 cursor-not-allowed' :
                                isRecording && activeQid === qid ? 'bg-red-500 hover:bg-red-600 shadow-[0_0_0_10px_rgba(239,68,68,0.15)]' : 'bg-white border border-primary hover:bg-primary/5'}
                ${isTranscribing && activeQid === qid ? 'opacity-60 cursor-wait' : ''}`}
                    type="button"
                    aria-label={
                        !isMicrophoneSupported() ? 'Microphone not supported' :
                            isRecording && activeQid === qid ? 'Stop recording' : 'Use microphone for speech to text'
                    }
                    onClick={() => !isTranscribing && isMicrophoneSupported() && handleMicClick(qid)}
                    disabled={isTranscribing || !isMicrophoneSupported()}
                    title={!isMicrophoneSupported() ? 'Microphone is not supported on this device or browser' : undefined}
                >
                    {!isMicrophoneSupported() ? (
                        <Mic className="w-6 h-6 sm:w-7 sm:h-7 text-gray-400" />
                    ) : isRecording && activeQid === qid ? (
                        <div className="flex items-end gap-[3px]">
                            {[0.45, 0.7, 1, 0.7].map((factor, index) => {
                                const height = 10 + Math.round(micLevel * factor * 16);
                                return (
                                    <span
                                        key={index}
                                        className="w-[3px] rounded-full bg-white transition-all duration-100"
                                        style={{ height: `${height}px`, opacity: 0.7 + factor * 0.3 }}
                                    />
                                );
                            })}
                            <StopCircle className="ml-1 w-5 h-5 text-white" />
                        </div>
                    ) : isTranscribing && activeQid === qid ? (
                        <svg className="animate-spin w-6 h-6 sm:w-7 sm:h-7 text-primary" viewBox="0 0 24 24">
                            <circle
                                className="opacity-25"
                                cx="12" cy="12" r="10"
                                stroke="currentColor" strokeWidth="4" fill="none"
                            />
                            <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                            />
                        </svg>
                    ) : (
                        <Mic className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
                    )}
                </button>
            </div>
            {/* Recording timer */}
            {isRecording && activeQid === qid && (
                <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground mt-1 sm:justify-start">
                    <span className="inline-flex h-2.5 w-2.5 rounded-full bg-red-500 animate-pulse" />
                    Recording: {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
                </div>
            )}
            {/* Transcription loading */}
            {isTranscribing && activeQid === qid && (
                <div className="text-xs sm:text-sm text-muted-foreground mt-2 flex items-center justify-center sm:justify-start gap-2">
                    Transcribing audio...
                </div>
            )}
            {q.expected && (
                <div className="mt-4">
                    <div className="flex justify-center sm:justify-end">
                        <button
                            className="flex items-center gap-2 text-xs sm:text-sm text-primary hover:underline"
                            type="button"
                            onClick={() => setShowHints((prev) => ({ ...prev, [qid]: !prev[qid] }))}
                        >
                            <Lightbulb className="w-4 h-4" />
                            {showHints[qid] ? 'Hide Hint' : 'Show Hint'}
                        </button>
                    </div>
                    {showHints[qid] && (
                        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mt-2 rounded">
                            <span className="text-xs sm:text-sm text-yellow-900 break-words">{q.expected}</span>
                        </div>
                    )}
                </div>
            )}
            
            {/* Navigation buttons */}
            <div className="w-full flex flex-col sm:flex-row justify-between gap-3 sm:gap-2 mt-6">
                <button
                    className="flex items-center justify-center gap-2 px-4 py-3 sm:py-2 rounded-md border font-medium text-primary hover:bg-primary/10 transition disabled:opacity-50 order-2 sm:order-1"
                    disabled={false}
                    onClick={goToPrev}
                >
                    <ArrowLeft className="w-4 h-4" /> Back
                </button>
                    <button
                        className="flex items-center justify-center gap-2 px-4 py-3 sm:py-2 rounded-md bg-primary text-white font-medium shadow hover:bg-primary/90 transition disabled:opacity-50 order-1 sm:order-2"
                        onClick={goToNext}
                        // Next/Finish always enabled, as skip is allowed
                        disabled={false}
                    >
                        {currentIdx + 1 === total ? 'Finish' : 'Next'} <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
}
    // Render modals at component level so they're always available
    return (
        <>
            {renderCurrentScreen()}
            
            {/* Unsaved Changes Warning Modal */}
            <AlertDialog open={showUnsavedWarning} onOpenChange={setShowUnsavedWarning}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
                        <AlertDialogDescription>
                            You have unsaved changes to your interview answers. What would you like to do?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                        <AlertDialogCancel onClick={handleUnsavedCancel}>
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleUnsavedDiscard}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            Discard Changes
                        </AlertDialogAction>
                        <AlertDialogAction onClick={handleUnsavedConfirm}>
                            Save & Continue
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Submit Warning Modal */}
            <AlertDialog open={showSubmitWarning} onOpenChange={setShowSubmitWarning}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Unanswered Questions</AlertDialogTitle>
                        <AlertDialogDescription>
                            You have some unanswered questions. Are you sure you want to submit your interview now? You can always go back to complete them.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                        <AlertDialogCancel onClick={handleSubmitCancel}>
                            Go Back
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={handleSubmitConfirm}>
                            Submit Interview
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

export default InterviewExam;
